<script lang="ts">
	// The design system's PasscodeDots (design/components/core/PasscodeDots.jsx):
	// eight positions between the heading and the pad.
	//
	//   idle    empty rings fill solid as digits arrive.
	//   error   all of them turn amber and stay until the next digit, which
	//           clears them and becomes digit one, so a cashier who looks up
	//           late still sees what happened. Amber, not red: a wrong code is
	//           something to know, and red means action.
	//   locked  the positions become dashes: the field is closed, not empty.
	//           The page disables the pad and passes the countdown in message.
	//
	// reveal is for the pairing screen only, never a passcode. The code there
	// is read off the owner's phone and is not secret, and the cashier has to
	// compare it with what they typed, which a dot cannot do. Each position
	// becomes a fixed-width mono cell over a slot rule, the same width for W
	// as for 2, so typing never shifts the row. A wrong code keeps its
	// characters and turns them amber, so the wrong one can be found; locked
	// is the same dashes.
	//
	// The row is left to right, like the pad. The message line keeps its
	// height when empty, so a message arriving or leaving never moves the pad
	// under a finger.
	//
	// The app adds test ids. A filled dot also carries the class `filled`, the
	// hook the unlock test counts to prove the pad cleared.

	interface Props {
		filled?: number;
		/** Shows each typed character instead of a dot. The pairing screen only. */
		reveal?: boolean;
		/** What has been typed, when reveal is on. */
		value?: string;
		state?: 'idle' | 'error' | 'locked';
		message?: string;
		length?: number;
		/** What the row is, for a screen reader. The design's default is the count. */
		label?: string;
		dotsTestId?: string;
		/** Carried by the message line only while it has something to say. */
		messageTestId?: string;
	}

	let {
		filled = 0,
		reveal = false,
		value = '',
		state = 'idle',
		message = '',
		length = 8,
		label,
		dotsTestId,
		messageTestId,
	}: Props = $props();

	const chars = $derived(reveal ? value.slice(0, length).split('') : []);
	const count = $derived(reveal ? chars.length : filled);
	const positions = $derived(Array.from({ length }, (_, index) => index < count));
	const spoken = $derived(
		label ?? (reveal ? chars.join(' ') || `0 / ${length}` : `${count} / ${length}`),
	);
</script>

<div class="passcode">
	<div class="dots" class:reveal dir="ltr" role="img" aria-label={spoken} data-testid={dotsTestId}>
		{#each positions as on, index (index)}
			{#if reveal}
				<span
					class="cell"
					class:on
					class:error={state === 'error'}
					class:locked={state === 'locked'}
				>
					{#if state === 'locked'}<span class="dash"></span>{:else if on}{chars[index]}{/if}
				</span>
			{:else}
				<span
					class="dot"
					class:filled={state === 'idle' && on}
					class:error={state === 'error'}
					class:locked={state === 'locked'}
				></span>
			{/if}
		{/each}
	</div>
	<p
		class="message"
		class:error={state === 'error'}
		role={state === 'idle' ? 'status' : 'alert'}
		aria-live={state === 'idle' ? 'polite' : 'assertive'}
		data-testid={state === 'idle' ? undefined : messageTestId}
	>
		{message}
	</p>
</div>

<style>
	.passcode {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 14px;
	}

	.dots {
		display: flex;
		align-items: center;
		gap: 14px;
		block-size: 14px;
	}

	.dots.reveal {
		gap: 8px;
		block-size: 44px;
	}

	.cell {
		flex: none;
		inline-size: 32px;
		block-size: 44px;
		box-sizing: border-box;
		display: grid;
		place-items: center;
		border-block-end: 2px solid var(--text-muted);
		font-family: var(--font-mono);
		font-size: 26px;
		font-weight: 700;
		line-height: 1;
		color: var(--text-strong);
	}

	.cell.on {
		border-block-end-color: var(--text-strong);
	}

	.cell.error {
		border-block-end-color: var(--surface-flag-solid);
		color: var(--ink-flag-deep);
	}

	.cell.locked {
		border-block-end: none;
	}

	.dash {
		inline-size: 14px;
		block-size: 2px;
		border-radius: 1px;
		background: var(--text-muted);
	}

	.dot {
		flex: none;
		inline-size: 14px;
		block-size: 14px;
		box-sizing: border-box;
		border-radius: var(--radius-pill);
		border: 2px solid var(--text-muted);
		background: transparent;
	}

	.dot.filled {
		border-color: var(--text-strong);
		background: var(--text-strong);
	}

	/* A fill pigment, so it holds in both themes like every amber fill. */
	.dot.error {
		border: none;
		background: var(--surface-flag-solid);
	}

	.dot.locked {
		block-size: 2px;
		border: none;
		border-radius: 1px;
		background: var(--text-muted);
	}

	.message {
		margin: 0;
		min-block-size: 24px;
		font-size: 15px;
		line-height: 24px;
		text-align: center;
		color: var(--text-body);
		font-weight: 500;
	}

	.message.error {
		color: var(--ink-flag-deep);
		font-weight: 600;
	}
</style>
